/**
 * AreaChart Component
 * Reusable area chart with stacking and curve customization
 */

"use client";

import { ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import {
  Area,
  CartesianGrid,
  Legend,
  AreaChart as RechartsAreaChart,
  XAxis,
  YAxis,
} from "recharts";
import { ChartWrapper } from "./ChartWrapper";
import { AreaChartProps, BaseChartData } from "./types";
import { formatAxisTick } from "./utils";

export const AreaChart = ({
  data,
  config,
  dataKeys,
  xAxisKey,
  title,
  description,
  footer,
  className,
  containerClassName,
  height,
  stacked = false,
  curveType = "natural",
  showGrid = true,
  showXAxis = true,
  showYAxis = true,
  showTooltip = true,
  showLegend = false,
  tooltipConfig,
  legendConfig,
  strokeWidth = 1,
  strokeWidths,
  showDots = false,
  accessibilityLayer = true,
  metricValue,
  metricChange,
  showMetricLayout = false,
  headerActions,
  xAxisFormatter,
  yAxisFormatter,
}: AreaChartProps) => {
  // Wrapper for formatAxisTick to match Recharts signature
  const formatXAxisTick = (value: any) =>
    xAxisFormatter ? xAxisFormatter(value) : formatAxisTick(value);
  const formatYAxisTick = (value: any) =>
    yAxisFormatter ? yAxisFormatter(value) : formatAxisTick(value, true);

  const chartContent = (
    <RechartsAreaChart
      accessibilityLayer={accessibilityLayer}
      data={data}
      margin={{ top: 10, right: 12, left: -20, bottom: 0 }}
    >
      {showGrid && <CartesianGrid vertical={false} />}

      {showXAxis && (
        <XAxis
          dataKey={xAxisKey}
          tickLine={false}
          axisLine={false}
          tickMargin={8}
          minTickGap={32}
          tickFormatter={formatXAxisTick}
        />
      )}

      {showYAxis && (
        <YAxis
          tickLine={false}
          axisLine={false}
          tickMargin={8}
          tickFormatter={formatYAxisTick}
        />
      )}

      {showTooltip && (
        <ChartTooltip
          cursor={tooltipConfig?.cursor ?? false}
          content={
            <ChartTooltipContent
              indicator={tooltipConfig?.indicator ?? "dot"}
              hideLabel={tooltipConfig?.hideLabel}
              nameKey={tooltipConfig?.nameKey}
            />
          }
          defaultIndex={tooltipConfig?.defaultIndex}
        />
      )}

      {showLegend && (
        <Legend
          verticalAlign={legendConfig?.position || "top"}
          height={36}
          iconType={legendConfig?.iconType || "line"}
        />
      )}

      <defs>
        {dataKeys.map((key: keyof BaseChartData, index: number) => {
          const configKey = String(key);

          // Use color from config, fallback to dynamic chart color if not defined
          const configColor = config[configKey]?.color;
          const fallbackChartNumber = (index % 5) + 1;
          const chartVariable =
            configColor || `var(--chart-${fallbackChartNumber})`;

          return (
            <linearGradient
              key={`gradient-${configKey}`}
              id={`fill${
                configKey.charAt(0).toUpperCase() + configKey.slice(1)
              }`}
              x1="0"
              y1="0"
              x2="0"
              y2="1"
            >
              <stop offset="5%" stopColor={chartVariable} stopOpacity={0.8} />
              <stop offset="95%" stopColor={chartVariable} stopOpacity={0.1} />
            </linearGradient>
          );
        })}
      </defs>

      {dataKeys.map((key: keyof BaseChartData, index: number) => {
        const configKey = String(key);
        const dynamicStrokeWidth = strokeWidths?.[configKey] ?? strokeWidth;

        // Use color from config, fallback to dynamic chart color if not defined
        const configColor = config[configKey]?.color;
        const fallbackChartNumber = (index % 5) + 1;
        const chartVariable =
          configColor || `var(--chart-${fallbackChartNumber})`;
        const gradientId = `fill${
          configKey.charAt(0).toUpperCase() + configKey.slice(1)
        }`;

        return (
          <Area
            key={configKey}
            type={curveType}
            dataKey={key}
            stackId={stacked ? "stack" : undefined}
            stroke={chartVariable}
            fill={`url(#${gradientId})`}
            strokeWidth={dynamicStrokeWidth}
            dot={showDots}
          />
        );
      })}
    </RechartsAreaChart>
  );

  return (
    <ChartWrapper
      title={title}
      description={description}
      footer={footer}
      className={className}
      containerClassName={containerClassName}
      config={config}
      height={height}
      metricValue={metricValue}
      metricChange={metricChange}
      showMetricLayout={showMetricLayout}
      headerActions={headerActions}
    >
      {chartContent}
    </ChartWrapper>
  );
};

AreaChart.displayName = "AreaChart";

export default AreaChart;
